import { useEffect, useState } from "react";

// Seleção de plano (catálogo local do Assistant - GET /api/plans).
// Usada no cadastro do tenant (/criar-conta), no gate pós-login do admin de
// org SEM plano e (como leitura) onde mais precisar. Toda organização precisa
// de um plano vinculado.

export interface PlanInfo {
  id: string;
  name: string;
  priceBrlMonthly: number;
  highlights: string[];
}

/** Catálogo público + dias de teste grátis (fonte dos textos "X dias grátis"). */
export function usePlanCatalog(): { plans: PlanInfo[]; trialDays: number } {
  const [plans, setPlans] = useState<PlanInfo[]>([]);
  const [trialDays, setTrialDays] = useState(14);
  useEffect(() => {
    let alive = true;
    fetch("/api/plans")
      .then((r) => (r.ok ? r.json() : { plans: [] }))
      .then((j: { plans?: PlanInfo[]; trialDays?: number }) => {
        if (!alive) return;
        setPlans(j.plans ?? []);
        if (typeof j.trialDays === "number") setTrialDays(j.trialDays);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);
  return { plans, trialDays };
}

export function usePlans(): PlanInfo[] {
  return usePlanCatalog().plans;
}

export function fmtBRL(v: number): string {
  return v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

export function PlanPicker({
  plans,
  value,
  onChange,
}: {
  plans: PlanInfo[];
  value: string;
  onChange: (id: string) => void;
}) {
  if (plans.length === 0) return null;
  return (
    <div className="plan-picker">
      {plans.map((p) => (
        <button
          key={p.id}
          type="button"
          className={`plan-card${value === p.id ? " selected" : ""}`}
          onClick={() => onChange(p.id)}
        >
          <span className="plan-card-name">{p.name}</span>
          <span className="plan-card-price">
            {fmtBRL(p.priceBrlMonthly)}<small>/mês</small>
          </span>
          <ul className="plan-card-list">
            {p.highlights.map((h) => (
              <li key={h}>{h}</li>
            ))}
          </ul>
        </button>
      ))}
    </div>
  );
}

/**
 * Gate pós-login: admin de organização SEM plano escolhe um antes de seguir
 * (overlay bloqueante). Orgs criadas antes da obrigatoriedade caem aqui.
 */
export function PlanSelectGate({ onDone }: { onDone: () => void }) {
  const plans = usePlans();
  const [planId, setPlanId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirm = async () => {
    if (!planId) return;
    setBusy(true);
    setError(null);
    try {
      const r = await fetch("/api/admin/org/plan", {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ planId }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError((j as { error?: string }).error ?? "falha ao definir o plano");
        return;
      }
      onDone();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay" role="dialog" aria-label="Escolha o plano da organização">
      <div className="modal plan-gate-modal">
        <h2>Escolha o plano da sua organização</h2>
        <p className="admin-sub">
          Toda organização precisa de um plano ativo - ele define usuários, repositórios e o orçamento de IA.
          Você pode mudar depois falando com o nosso time.
        </p>
        <PlanPicker plans={plans} value={planId} onChange={setPlanId} />
        {error && <p className="auth-error">{error}</p>}
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
          <button className="btn-approve" type="button" disabled={busy || !planId} onClick={() => void confirm()}>
            {busy ? "Salvando…" : "Confirmar plano"}
          </button>
        </div>
      </div>
    </div>
  );
}
